import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'browser-tips');

  const currentCategory = useMemo(
    () => categories.find((cat) => cat.slug === activeCategory),
    [activeCategory]
  );

  const filteredArticles = useMemo(
    () => articles.filter((article) => article.category === activeCategory),
    [activeCategory]
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-foreground-600 mb-4">请检查分类地址是否正确</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 91网页浏览器`}
        description={currentCategory.description}
        keywords={`91网页浏览器,${currentCategory.name},网页浏览器,浏览器使用技巧,安全上网`}
        canonicalUrl={`https://www.91browser.com/category/${activeCategory}`}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100/50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        <section className="py-12 md:py-16 bg-background-100/50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className={`${currentCategory.icon} text-primary-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-foreground-600 text-base leading-relaxed">
                {currentCategory.description}
              </p>
            </div>

            <div className="flex items-center gap-2 mb-10 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200/60'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {cat.name}
                  </span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-background-300/60 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`${article.title} - 91网页浏览器`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="font-heading font-semibold text-foreground-900 mb-2 text-base leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <span>·</span>
                      <span>{article.readTime}阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-article-line text-foreground-400 text-2xl w-7 h-7 flex items-center justify-center"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}